import React, { useCallback, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { CircularProgress, IconButton, Typography } from '@material-ui/core';

import * as ls from 'src/lib/localData';
import { formatRoute } from 'src/lib/routing';
import { ROUTES, ROUTE_PARAMS } from 'src/routes';
import { useHistory } from 'src/services';
import { nthNumber } from 'src/utils/format';
import { Button } from 'src/components/Button';
import { getChannelPerformanceLabel, CHANNEL } from 'src/data/channel';
import { useUpgradePopup } from 'src/services/popup';
import { UPGRADE_DIALOG_ACTIONS_ORIGIN } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { EMAIL_CHANNEL, RESOURCE_DOCUMENT, getResourceDocumentLabel } from 'src/data/performance';
import { SUBSCRIPTION_PLAN } from 'src/data/subscription';
import {
  getChannelItemLabel,
  getResourceChannelsFromAssetType,
  renderUserGoal,
  SOURCE,
} from 'src/data/benchmark';
import { TruncatedSpan } from 'src/components/TruncatedSpan';
import IconSettings from 'src/assets/icons/Settings';
import { IconCloseThin } from 'src/assets/icons/CloseThin';
import { IconSearchTimes } from 'src/assets/icons/IconSearchTimes';
import { Loader } from './Loader';
import { useStyles } from './useStyles';

export const Benchmark = ({
  onBenchmarkConnectClick,
  channel,
  benchmarkConfig,
  selectedAccountIds,
  extractingCopies,
  loadingCopies,
  loadingBenchmark,
  listenToIntegrationExtraction,
  source,
  resourcesCSVDocumentsAccounts,
  shouldUpgrade,
  variationId,
  projectId,
  isHighlighted,
  featureLimitsData,
  openPopup,
  onBenchmarkUpgradeClick,
  assetType,
}) => {
  const styles = useStyles();

  const history = useHistory();

  const [showUpgradeSection, setShowUpgradeSection] = useState(shouldUpgrade);

  const isEmailChannel = channel === CHANNEL.hubspot;

  const { limit, currentCount, limitedAccountData } = featureLimitsData;
  const isFeatureLimited = limit && (!!limitedAccountData?.accountId || limit <= currentCount);
  const openUpgradePopup = useUpgradePopup({
    actionsOrigin: UPGRADE_DIALOG_ACTIONS_ORIGIN.cipPageConnectDialog,
    projectId,
    variationId,
  });

  const isEmailChannelConnected =
    resourcesCSVDocumentsAccounts?.filter((a) => {
      const resourceChannels = getResourceChannelsFromAssetType(assetType);
      return resourceChannels.includes(a.channel);
    }).length > 0;

  const isChannelConnected =
    selectedAccountIds?.[channel]?.length > 0 ||
    source === SOURCE.sharedPage ||
    (isEmailChannel && isEmailChannelConnected);

  const formatIntegrationLink = useCallback(() => {
    if (projectId && variationId) {
      return formatRoute(ROUTES.INTEGRATIONS_RESOURCES, {
        [ROUTE_PARAMS.integration]: isEmailChannel ? EMAIL_CHANNEL : channel,
        projectId,
        variationId,
      });
    } else {
      return formatRoute(ROUTES.INTEGRATIONS_RESOURCES, {
        [ROUTE_PARAMS.integration]: isEmailChannel ? EMAIL_CHANNEL : channel,
      });
    }
  }, [isEmailChannel, channel, projectId, variationId]);

  const handleConnectClick = useCallback(() => {
    onBenchmarkConnectClick?.();
    if (!isChannelConnected && isFeatureLimited) {
      openUpgradePopup(FEATURE_LIMITS.resourcesRowsPerCustomer);
      onBenchmarkUpgradeClick();
      return;
    }
    const link = formatIntegrationLink();
    if (listenToIntegrationExtraction) {
      window.open(link);
      if (isEmailChannel) {
        listenToIntegrationExtraction(channel);
        listenToIntegrationExtraction(RESOURCE_DOCUMENT.emailSubjectLineCsvResource);
        listenToIntegrationExtraction(RESOURCE_DOCUMENT.emailColdCsvResource);
        listenToIntegrationExtraction(RESOURCE_DOCUMENT.emailPromotionCsvResource);
      } else {
        listenToIntegrationExtraction(channel);
      }
    } else {
      history.push(link);
    }
  }, [
    onBenchmarkUpgradeClick,
    listenToIntegrationExtraction,
    isEmailChannel,
    formatIntegrationLink,
    channel,
    onBenchmarkConnectClick,
    isChannelConnected,
    openUpgradePopup,
    isFeatureLimited,
    history,
  ]);

  const handleUpgradeClick = () => {
    onBenchmarkUpgradeClick();
    window.open(
      formatRoute(ROUTES.CHECKOUT, {
        plan: SUBSCRIPTION_PLAN.business_self_serve,
        projectId,
        variationId,
      })
    );
  };

  const handleCloseUpgradeBannerClick = () => {
    setShowUpgradeSection(false);
    ls.insightsPanelCounter.write(0);
  };

  const isAnyCsvResourcesExtracting = Object.values(
    extractingCopies?.[RESOURCE_DOCUMENT.emailSubjectLineCsvResource] ||
      extractingCopies?.[RESOURCE_DOCUMENT.emailPromotionCsvResource] ||
      extractingCopies?.[RESOURCE_DOCUMENT.emailColdCsvResource] ||
      {}
  ).some((r) => r);

  const isChannelExtracting = extractingCopies?.[channel];

  const isChannelLoading = loadingCopies?.[channel];

  const isExtractingChannelCopies = isEmailChannel
    ? isChannelExtracting || isAnyCsvResourcesExtracting
    : isChannelExtracting;

  const isLoadingChannelCopies = isEmailChannel
    ? isChannelLoading ||
      loadingCopies?.[RESOURCE_DOCUMENT.emailSubjectLineCsvResource] ||
      loadingCopies?.[RESOURCE_DOCUMENT.emailPromotionCsvResource] ||
      loadingCopies?.[RESOURCE_DOCUMENT.emailColdCsvResource]
    : isChannelLoading;

  const isExtracting = isExtractingChannelCopies || isLoadingChannelCopies;

  const hasPosition = benchmarkConfig?.position > 0;

  const channelLabel =
    getChannelPerformanceLabel(benchmarkConfig?.account?.channel) ||
    getResourceDocumentLabel(benchmarkConfig?.account?.channel) ||
    getChannelPerformanceLabel(channel);

  const connectedAndHasBenchmark =
    isChannelConnected &&
    benchmarkConfig?.benchmarks?.length > 0 &&
    hasPosition &&
    !benchmarkConfig?.shouldUpgrade &&
    !isExtracting;

  const shouldShowUpgradeSection = showUpgradeSection && connectedAndHasBenchmark;

  const emptyResults =
    (!(hasPosition || isExtracting) && isChannelConnected && !benchmarkConfig?.shouldUpgrade) ||
    benchmarkConfig?.shouldUpgrade;

  const accountLabel = `${channelLabel} (${benchmarkConfig?.account?.name}) ${getChannelItemLabel(
    channel
  )}`;

  if (loadingBenchmark) {
    return (
      <div className={styles.root}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div
        className={cx(styles.container, {
          [styles.shouldUpgrade]: shouldShowUpgradeSection,
          [styles.highlighted]: isHighlighted,
          [styles.connected]: connectedAndHasBenchmark,
          [styles.empty]: emptyResults,
          [styles.isInfinity]: source === SOURCE.infinity,
        })}
        onClick={connectedAndHasBenchmark ? openPopup : undefined}
      >
        {!(isChannelConnected || isExtracting) && (
          <>
            <Typography className={styles.title}># of 10</Typography>
            <Typography className={styles.subtitle}>
              See how this text compares with text you published in the past. Connect your{' '}
              {getChannelPerformanceLabel(channel)} account.
            </Typography>
            <Button
              color="primary"
              variant="text"
              className={styles.connectButton}
              onClick={handleConnectClick}
            >
              Connect
            </Button>
          </>
        )}

        {isExtracting && (
          <>
            <Typography className={styles.title}># of 10</Typography>
            <Typography className={styles.subtitle}>
              Please wait while we process your data...
            </Typography>

            <CircularProgress
              className={styles.loader}
              size={16}
            />
          </>
        )}

        {connectedAndHasBenchmark && (
          <>
            <Typography className={styles.title}>
              #{benchmarkConfig.position} of {benchmarkConfig.benchmarks.length}
            </Typography>
            <Typography className={styles.subtitle}>
              <span>
                Projected to perform ({renderUserGoal(benchmarkConfig.userGoal)}){' '}
                {nthNumber(benchmarkConfig.position)} best of {benchmarkConfig.benchmarks.length}{' '}
                similar texts{' '}
              </span>
              <TruncatedSpan
                className={styles.accountName}
                title={accountLabel}
              >
                from {accountLabel}.
              </TruncatedSpan>
            </Typography>

            <IconButton
              onClick={connectedAndHasBenchmark ? null : openPopup}
              className={styles.settingsButton}
            >
              <IconSettings />
            </IconButton>
          </>
        )}

        {emptyResults && (
          <>
            <IconSearchTimes className={styles.iconSearch} />
            <Typography className={styles.subtitleEmpty}>
              <b>Oops, we couldn't find relevant copy to benchmark against.</b>
              <br />
              Please make sure that your connected account contains published copy that is similar
              to this text.
            </Typography>
            <IconButton
              onClick={openPopup}
              className={styles.settingsButton}
            >
              <IconSettings />
            </IconButton>
          </>
        )}
      </div>
      {shouldShowUpgradeSection && (
        <div className={styles.upgradeWrapper}>
          <Typography className={styles.upgradeText}>
            Your current Starter subscription allows only limited use of your data (50 rows).
            Upgrade to Business tier to benchmark this text against all of your data.
          </Typography>
          <Button
            color="primary"
            variant="text"
            className={styles.upgradeButton}
            onClick={handleUpgradeClick}
          >
            Upgrade
          </Button>
          <IconButton
            className={styles.closeButton}
            onClick={() => handleCloseUpgradeBannerClick(false)}
          >
            <IconCloseThin />
          </IconButton>
        </div>
      )}
    </div>
  );
};

Benchmark.propTypes = {
  loading: PropTypes.bool,
  channel: PropTypes.string.isRequired,
  onBenchmarkConnectClick: PropTypes.func,
  benchmarkConfig: PropTypes.shape({
    userGoal: PropTypes.string,
    account: PropTypes.shape({
      name: PropTypes.string,
      channel: PropTypes.string,
    }),
    position: PropTypes.number,
    benchmarks: PropTypes.array,
    loadingGlobalBenchmark: PropTypes.bool,
    shouldUpgrade: PropTypes.bool,
  }),
  selectedAccountIds: PropTypes.shape({}),
  extractingCopies: PropTypes.shape({}),
  loadingCopies: PropTypes.shape({}),
  loadingBenchmark: PropTypes.bool,
  listenToIntegrationExtraction: PropTypes.func.isRequired,
  source: PropTypes.string,
  resourcesCSVDocumentsAccounts: PropTypes.arrayOf(PropTypes.shape({})),
  shouldUpgrade: PropTypes.bool,
  variationId: PropTypes.string,
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isHighlighted: PropTypes.bool,
  featureLimitsData: PropTypes.shape({
    limit: PropTypes.number,
    currentCount: PropTypes.number,
    limitedAccountData: PropTypes.shape({
      accountId: PropTypes.string,
    }),
  }),
  openPopup: PropTypes.func.isRequired,
  onBenchmarkUpgradeClick: PropTypes.func.isRequired,
  assetType: PropTypes.string,
};

Benchmark.defaultValues = {
  loading: null,
  selectedAccountIds: null,
  onBenchmarkConnectClick: null,
  benchmarkConfig: null,
  extractingCopies: null,
  loadingCopies: null,
  loadingBenchmark: false,
  listenToIntegrationExtraction: null,
  source: null,
  resourcesCSVDocumentsAccounts: null,
  shouldUpgrade: false,
  variationId: null,
  projectId: null,
  isHighlighted: false,
  featureLimitsData: null,
  assetType: null,
};
